// Import CSS
import './AssessmentForm.scss'

// React and Third Party Libraries
import { useCallback, useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'

import QuizProgressBar from 'components/quizzes/QuizProgressBar'
import AssessmentFormScreen from '../AssessmentFormScreen'

import {
  EVENT_ACTION_BACK,
  EVENT_ACTION_CONTINUE,
  EVENT_CATEGORY_ASSESSMENT,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { KEY_PRACTICES_PREFERENCES_FIELD } from 'components/common/api'
import { AssessmentFormProvider } from 'components/common/contexts/AssessmentFormContext'
import PracticesInfo from 'components/welcome/PracticesInfo/PracticesInfo'

/**
 * Assessment Form component: used to render an assessment form, which follows the assessment schema
 * @param {Object} initProps - initial props
 * @param {Function} initProps.assessmentCompleteCallback - callback function to be called when the assessment is complete
 * @param {Object} initProps.formData - form data to be used to pre-populate the form. Must follow the assessment schema
 * @returns {JSX.Element} - Assessment Form component
 */
export default function AssessmentForm({
  assessmentCompleteCallback,
  formData,
  userPreferences = {}
}) {
  // State
  const [readyToProceed, setReadyToProceed] = useState(false)
  const [activeScreenIndex, setActiveScreenIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [validScreens, setValidScreens] = useState([])

  const screens = formData?.screens || []
  const totalScreens = screens.length

  /**
   * Helper function to determine if a screen meets the conditions to be shown
   * @param {Object} condition - condition object
   * @returns {Boolean} - true if the conditions are met, false otherwise
   */
  const conditionIsSatisfied = condition => {
    // If the screen has no conditions, return true
    if (!condition) return true

    // Determine the next screen index based on the conditions
    if (condition.name) {
      const answer = answers[condition.name]

      // If the condition has a name and an `is` property, let's check the answer
      if (condition.is) {
        // If the answer is an array, check if the `is` array includes any of the values in the answer array
        if (Array.isArray(answer)) {
          if (answer.some(value => condition.is.includes(value))) return true

          // Otherwise, check if the answer is one of the values in the `is` array
        } else if (condition.is.includes(answer)) {
          return true
        }
      }

      // If the condition has a name and a `not` property, check if the answer is not one of the values in the `not` array
      if (condition.not && !condition.not.includes(answer)) return true

      // If the condition has a name and a `length` property, check the `lenghth.min` subproperty to determine if the length of the value of the answer
      // is greater than or equal to the `length.min` value
      if (condition.length?.min && answer.length >= condition.length.min)
        return true
    }
  }

  /**
   * Helper function to determine the next screen index, based on the current screen index and by checking the conditions of the next screen
   * @param {Number} currentIndex - current screen index
   * @returns {Number|null} - next screen index or null if the next screen index is not found
   */
  const getNextScreenIndex = currentIndex => {
    // Get the next screen index
    const nextScreenIndex = currentIndex + 1

    // If the next screen index is greater than the total number of screens, return null
    if (nextScreenIndex >= totalScreens) return null

    // Get the next screen
    const nextScreen = screens[nextScreenIndex]

    // If the next screen has conditions, check if the conditions are satisfied
    // If the conditions are satisfied, return the next screen index
    if (conditionIsSatisfied(nextScreen.condition)) return nextScreenIndex

    // If we don't have a next screen index, increment the current screen index and try again
    return getNextScreenIndex(nextScreenIndex)
  }

  /**
   * Helper function to determine the previous screen index, based on the current screen index and by checking the conditions of the previous screen
   * @param {Number} currentIndex - current screen index
   * @returns {Number|null} - previous screen index or null if the previous screen index is not found
   */
  const getPreviousScreenIndex = currentIndex => {
    // Get the previous screen index
    const previousScreenIndex = currentIndex - 1

    // If the previous screen index is less than 0, return null
    if (previousScreenIndex < 0) return null

    // Get the previous screen
    const previousScreen = screens[previousScreenIndex]

    // If the previous screen has conditions, check if the conditions are satisfied
    // If the conditions are satisfied, return the screen index
    if (conditionIsSatisfied(previousScreen.condition))
      return previousScreenIndex

    // If we don't have a previous screen index, decrement the current screen index and try again
    return getPreviousScreenIndex(previousScreenIndex)
  }

  /**
   * Helper function to set the screen as valid or invalid
   * @param {Number} screenIndex - index of the screen
   * @param {Boolean} isValid - whether the screen is valid or not
   */
  const setScreenIsValid = useCallback(
    (screenIndex, isValid) => {
      // Don't update the valid screens if the screenIndex is not the active screen index
      if (screenIndex !== activeScreenIndex) return

      let updatedValidScreens = [...validScreens]
      if (isValid) {
        updatedValidScreens = [...validScreens, screenIndex]
      } else {
        updatedValidScreens = validScreens.filter(
          index => index !== screenIndex
        )
      }

      // Remove duplicates
      updatedValidScreens = [...new Set(updatedValidScreens)]

      // Sort the valid screens
      updatedValidScreens.sort((a, b) => a - b)

      // Update ready to proceed
      setReadyToProceed(updatedValidScreens.includes(activeScreenIndex))

      // Update valid screens
      setValidScreens(updatedValidScreens)
    },
    [activeScreenIndex, validScreens]
  )

  /**
   * Helper function to check if a given screen is optional. If it is, it will be added to the valid screens array
   * @param {Number} screenIndex - index of the screen to check
   * @returns {Boolean} - true if the screen is optional, false otherwise
   */
  const checkScreenIsOptional = screenIndex => {
    // Get a reference to the screen
    const screen = screens[screenIndex]
    const isOptional = screen?.optional
    if (isOptional) setScreenIsValid(screenIndex, true)

    // If the screen is not optional, return
    return isOptional
  }

  /**
   * Helper function to handle answer changes
   * @param {String} name The unique name of the question
   * @param {String|Array} answer The answer to the question
   */
  const handleAnswerChange = (name, answer) => {
    // If we don't have a name or answer, return
    if (!name || !answer) return

    setAnswers({
      ...answers,
      [name]: answer
    })
  }

  /**
   * Handle back button click
   * @param {Object} event - event object
   */
  const handleBack = event => {
    event.preventDefault()
    if (activeScreenIndex > 0) {
      // Decrement the active screen index
      const previousScreenIndex = getPreviousScreenIndex(activeScreenIndex)

      // If we don't have a previous screen index, return
      if (previousScreenIndex === null) return

      // Check if the previous screen is optional
      const isPreviousScreenOptional =
        checkScreenIsOptional(previousScreenIndex)
      const ready =
        isPreviousScreenOptional || validScreens.includes(previousScreenIndex)

      // Disable the continue button if the next screen is not valid
      setReadyToProceed(ready)
      setActiveScreenIndex(previousScreenIndex)

      // Log user event
      saveUserEvent(
        EVENT_CATEGORY_ASSESSMENT,
        EVENT_ACTION_BACK,
        'screen',
        previousScreenIndex
      )

      // Scroll to the top of the page using a timeout to ensure the scroll happens after the screen has been updated
      setTimeout(() => window.scrollTo(0, 0), 0)
    }
  }

  /**
   * Handle continue button click
   * @param {Object} event - event object
   */
  const handleContinue = event => {
    event.preventDefault()
    // If we're not ready to proceed, return
    if (!readyToProceed) return

    // If we're not on the last screen, increment the active screen index
    if (activeScreenIndex < totalScreens - 1) {
      // Increment the active screen index
      const nextScreenIndex = getNextScreenIndex(activeScreenIndex)

      // If we don't have a next screen index, return
      if (nextScreenIndex === null) return

      // Check if the next screen is optional
      const isNextScreenOptional = checkScreenIsOptional(nextScreenIndex)
      const ready =
        isNextScreenOptional || validScreens.includes(nextScreenIndex)

      // Disable the continue button if the next screen is not valid
      setReadyToProceed(ready)
      setActiveScreenIndex(nextScreenIndex)

      // Log user event
      saveUserEvent(
        EVENT_CATEGORY_ASSESSMENT,
        EVENT_ACTION_CONTINUE,
        'screen',
        nextScreenIndex
      )

      // Scroll to the top of the page using a timeout to ensure the scroll happens after the screen has been updated
      setTimeout(() => window.scrollTo(0, 0), 0)
    } else {
      // If we're on the last screen, call the assessment complete callback function
      assessmentCompleteCallback(answers)
    }
  }

  // If we don't have a callback function, or if we don't have form data, return null
  if (!assessmentCompleteCallback || !formData) return null

  return (
    <div className='assessment-form'>
      <QuizProgressBar
        total={totalScreens}
        completed={activeScreenIndex + 1}
        showCount={false}
      />
      <AssessmentFormProvider
        value={{
          answers,
          userPreferences,
          formData
        }}
      >
        {screens.map((screen, index) => (
          <AssessmentFormScreen
            screenData={screen}
            screenIndex={index}
            activeScreenIndex={activeScreenIndex}
            isVisible={index === activeScreenIndex}
            setScreenIsValid={setScreenIsValid}
            key={index}
            handleAnswerChange={handleAnswerChange}
          />
        ))}
      </AssessmentFormProvider>

      {/* If we're on the `practices-preferences` screen, show the "Not sure?" button */}
      {screens[activeScreenIndex]?.key === KEY_PRACTICES_PREFERENCES_FIELD && (
        <PracticesInfo />
      )}

      <Row className='assessment-form-navigation'>
        {/* Don't show "back" button on first screen */}
        {activeScreenIndex > 0 && (
          <Col xs={5}>
            <Button
              className='assessment-form-back-button'
              variant='light'
              size='lg'
              onClick={handleBack}
            >
              <i className='bi bi-arrow-left'></i> Back
            </Button>
          </Col>
        )}
        <Col>
          <Button
            className='assessment-form-continue-button w-100'
            variant='primary'
            size='lg'
            disabled={!readyToProceed}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </Col>
      </Row>
    </div>
  )
}
